import { inject, Service } from '@angular/core';
import { Observable } from 'rxjs';

import { CacheService } from './cache.service';
import {
  homeContentCacheKey,
  homeContentStartedAtCacheKey,
  translationsCacheKey,
  translationsStartedAtCacheKey,
} from '@core/constants/cache-keys.constants';
import { LoggerService, ScopedLogger } from '@core/logging/logger.service';
import { Language } from '@core/i18n/domain/language';

/** TTL + SSR flags a caller (which owns its config) hands to a cache operation. */
export interface LanguageCacheOptions {
  /** Cache lifetime in ms (`0`/omitted ⇒ no expiry). */
  ttlMs?: number | null;
  /** Hand SSR-fetched data to the client via TransferState (default: true). */
  useTransferState?: boolean;
  /** Persist to localStorage in the browser (default: true). */
  persistToStorage?: boolean;
}

/**
 * LanguageCacheService — the language-scoped specialization of
 * {@link CacheService} that every i18n / home facade uses.
 *
 * It owns two things the generic cache cannot:
 *   1. the language-scoped KEY for each domain (translations / home content), and
 *   2. the exact, audited LoggerService events the product requires
 *      (`Translation Cache Hit/Miss`, `… API Call`, `… Cached`, and the home
 *      equivalents).
 *
 * Cache freshness, SSR safety and de-duplication are all delegated to
 * CacheService — this layer is thin, declarative key + logging glue.
 *
 * Root-provided; callers pass their own TTL/SSR flags from their feature config.
 */
@Service()
export class LanguageCacheService {
  private readonly cache = inject(CacheService);
  private readonly logger: ScopedLogger = inject(LoggerService).forSource('LanguageCache');

  // ── translations ───────────────────────────────────────────────────────
  /**
   * Resolve translations for `lang` from cache, or run `fetchFn` (the API call)
   * and cache the result under `talbinah-portal-translations-{lang}-storage`.
   */
  getOrFetchTranslations<T>(
    lang: Language,
    fetchFn: () => Observable<T>,
    options: LanguageCacheOptions = {},
  ): Observable<T> {
    const key = translationsCacheKey(lang);
    return this.cache.getOrFetch<T>(
      key,
      () => {
        this.logger.translation('Translation API Call', { language: lang });
        return fetchFn();
      },
      {
        ttlMs: options.ttlMs,
        useTransferState: options.useTransferState,
        persistToStorage: options.persistToStorage,
        onHit: () => this.logger.translation('Translation Cache Hit', { language: lang }),
        onMiss: () => this.logger.translation('Translation Cache Miss', { language: lang }),
        onStored: () => {
          // Companion timestamp key — `…-{lang}-started-at` (parity with home content).
          this.cache.write<number>(translationsStartedAtCacheKey(lang), this.now(), {
            ttlMs: options.ttlMs,
            useTransferState: options.useTransferState,
            persistToStorage: options.persistToStorage,
          });
          this.logger.translation('Translation Cached', { language: lang });
        },
      },
    );
  }

  /** `true` iff fresh translations are cached for `lang` (non-mutating probe). */
  protected isTranslationsCached(lang: Language): boolean {
    return this.cache.isFreshKey(translationsCacheKey(lang));
  }

  /** Epoch ms the translations for `lang` were last fetched, or `null`. */
  protected getTranslationsStartedAt(lang: Language): number | null {
    return this.cache.read<number>(translationsStartedAtCacheKey(lang));
  }

  /** Drop the cached translations (and timestamp) for `lang` (forces a re-fetch). */
  invalidateTranslations(lang: Language): void {
    this.cache.remove(translationsCacheKey(lang));
    this.cache.remove(translationsStartedAtCacheKey(lang));
  }

  // ── home content ─────────────────────────────────────────────────────────
  /**
   * Resolve home content for `lang` from cache, or run `fetchFn` (the API call)
   * and cache it under `PatientTalbinahPortalMainPageHomeContent_{lang}`. On a
   * miss it also stamps `PatientTalbinahPortalMainPageHomeContentStartedAt_{lang}`.
   */
  getOrFetchHomeContent<T>(
    lang: Language,
    fetchFn: () => Observable<T>,
    options: LanguageCacheOptions = {},
  ): Observable<T> {
    const key = homeContentCacheKey(lang);
    return this.cache.getOrFetch<T>(
      key,
      () => {
        this.logger.home('Home Content API Call', { language: lang });
        return fetchFn();
      },
      {
        ttlMs: options.ttlMs,
        useTransferState: options.useTransferState,
        persistToStorage: options.persistToStorage,
        onHit: () => this.logger.home('Home Content Cache Hit', { language: lang }),
        onMiss: () => this.logger.home('Home Content Cache Miss', { language: lang }),
        onStored: () => {
          // Companion timestamp key (parity with the legacy back-office contract).
          this.cache.write<number>(homeContentStartedAtCacheKey(lang), this.now(), {
            ttlMs: options.ttlMs,
            useTransferState: options.useTransferState,
            persistToStorage: options.persistToStorage,
          });
          this.logger.home('Home Content Cached', { language: lang });
        },
      },
    );
  }

  /** `true` iff fresh home content is cached for `lang` (non-mutating probe). */
  isHomeContentCached(lang: Language): boolean {
    return this.cache.isFreshKey(homeContentCacheKey(lang));
  }

  /** Epoch ms the home content for `lang` was last fetched, or `null`. */
  protected getHomeContentStartedAt(lang: Language): number | null {
    return this.cache.read<number>(homeContentStartedAtCacheKey(lang));
  }

  /** Drop the cached home content (and its timestamp) for `lang`. */
  invalidateHomeContent(lang: Language): void {
    this.cache.remove(homeContentCacheKey(lang));
    this.cache.remove(homeContentStartedAtCacheKey(lang));
  }

  private now(): number {
    return Date.now(); // SSR-safe
  }
}
